import React, { useState } from 'react';
import {
  Box,
  Rating,
  TextField,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Chip,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Save as SaveIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';

const JobRating = ({ assignment, onRatingSubmit, onRatingUpdate, onRatingDelete, disabled = false }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [rating, setRating] = useState(assignment?.performanceRating || 0);
  const [notes, setNotes] = useState(assignment?.performanceNotes || '');
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const hasExistingRating = assignment?.performanceRating && assignment.performanceRating > 0;

  const handleOpenDialog = () => {
    setRating(assignment?.performanceRating || 0);
    setNotes(assignment?.performanceNotes || '');
    setError('');
    setIsEditing(hasExistingRating);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setIsEditing(false);
    setError('');
    setLoading(false);
  };

  const handleSubmit = async () => {
    if (rating < 1 || rating > 5) {
      setError('Please provide a rating between 1 and 5 stars');
      return;
    }

    setLoading(true);
    setError('');

    try {
      if (hasExistingRating) {
        await onRatingUpdate(assignment.id, rating, notes);
      } else {
        await onRatingSubmit(assignment.id, rating, notes);
      }
      handleCloseDialog();
    } catch (err) {
      console.error('Error submitting rating:', err);
      setError(err.message || 'Failed to save rating');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    setError('');

    try {
      await onRatingDelete(assignment.id);
      handleCloseDialog();
    } catch (err) {
      console.error('Error deleting rating:', err);
      setError(err.message || 'Failed to delete rating');
    } finally {
      setLoading(false);
    }
  };

  const getRatingColor = (ratingValue) => {
    if (ratingValue >= 4.5) return '#4caf50'; // Green
    if (ratingValue >= 3.5) return '#ff9800'; // Orange
    if (ratingValue >= 2.5) return '#f57c00'; // Dark Orange
    return '#f44336'; // Red
  };

  const getRatingText = (ratingValue) => {
    if (ratingValue >= 4.5) return 'Excellent';
    if (ratingValue >= 3.5) return 'Good';
    if (ratingValue >= 2.5) return 'Fair';
    if (ratingValue >= 1.5) return 'Poor';
    return 'Very Poor';
  };

  // Display component when rating exists
  if (hasExistingRating) {
    return (
      <>
        <Box display="flex" alignItems="center" gap={1}>
          <Rating
            value={assignment.performanceRating}
            readOnly
            size="small"
            icon={<StarIcon fontSize="inherit" />}
            emptyIcon={<StarBorderIcon fontSize="inherit" />}
          />
          <Typography variant="body2" sx={{ color: getRatingColor(assignment.performanceRating) }}>
            {assignment.performanceRating}/5 ({getRatingText(assignment.performanceRating)})
          </Typography>
          {!disabled && (
            <Tooltip title="Edit Rating">
              <IconButton size="small" onClick={handleOpenDialog}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        {assignment.performanceNotes && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
            "{assignment.performanceNotes}"
          </Typography>
        )}
        {assignment.ratedAt && (
          <Typography variant="caption" color="text.secondary" display="block">
            Rated: {new Date(assignment.ratedAt).toLocaleDateString()}
          </Typography>
        )}

        {/* Edit Rating Dialog */}
        <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          <DialogTitle>Edit Performance Rating</DialogTitle>
          <DialogContent>
            <Box sx={{ py: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Technician: {assignment.maidName || 'Unknown'}
              </Typography>
              
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <Box sx={{ mb: 3 }}>
                <Typography component="legend" gutterBottom>
                  Performance Rating *
                </Typography>
                <Rating
                  value={rating}
                  onChange={(event, newValue) => setRating(newValue || 0)}
                  size="large"
                  icon={<StarIcon fontSize="inherit" />}
                  emptyIcon={<StarBorderIcon fontSize="inherit" />}
                  disabled={loading}
                />
                {rating > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <Chip 
                      label={`${rating}/5 - ${getRatingText(rating)}`}
                      size="small"
                      sx={{ 
                        backgroundColor: getRatingColor(rating),
                        color: 'white'
                      }}
                    />
                  </Box>
                )}
              </Box>

              <TextField
                fullWidth
                multiline
                rows={3}
                label="Notes (Optional)"
                placeholder="Add notes about the performance..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                disabled={loading}
                inputProps={{ maxLength: 500 }}
                helperText={`${notes.length}/500 characters`}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDelete} color="error" disabled={loading}>
              Delete Rating
            </Button>
            <Button onClick={handleCloseDialog} disabled={loading}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit} 
              variant="contained" 
              disabled={loading || rating < 1}
              startIcon={loading ? <CircularProgress size={16} /> : <SaveIcon />}
            >
              Update Rating
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }

  // Display component when no rating exists
  return (
    <>
      <Box display="flex" alignItems="center" gap={1}>
        <Typography variant="body2" color="text.secondary">
          Not rated
        </Typography>
        {!disabled && (
          <Tooltip title="Add Performance Rating">
            <IconButton size="small" onClick={handleOpenDialog} color="primary">
              <AddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Add Rating Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Add Performance Rating</DialogTitle>
        <DialogContent>
          <Box sx={{ py: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Technician: {assignment.maidName || 'Unknown'}
            </Typography>
            
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Box sx={{ mb: 3 }}>
              <Typography component="legend" gutterBottom>
                Performance Rating *
              </Typography>
              <Rating
                value={rating}
                onChange={(event, newValue) => setRating(newValue || 0)}
                size="large"
                icon={<StarIcon fontSize="inherit" />}
                emptyIcon={<StarBorderIcon fontSize="inherit" />}
                disabled={loading}
              />
              {rating > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Chip 
                    label={`${rating}/5 - ${getRatingText(rating)}`}
                    size="small"
                    sx={{ 
                      backgroundColor: getRatingColor(rating),
                      color: 'white'
                    }}
                  />
                </Box>
              )}
            </Box>

            <TextField
              fullWidth
              multiline
              rows={3}
              label="Notes (Optional)"
              placeholder="Add notes about the performance..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={loading}
              inputProps={{ maxLength: 500 }}
              helperText={`${notes.length}/500 characters`}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={loading}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            disabled={loading || rating < 1}
            startIcon={loading ? <CircularProgress size={16} /> : <SaveIcon />}
          >
            Save Rating
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default JobRating;